import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useContentFeeOrders } from '@/hooks/admin/useContentFee'
import type { ContentFeeOrderRow } from '@/api/contentFee'

function statusClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-50 text-green-700 border border-green-200'
    case 'refunded':
      return 'bg-red-50 text-red-700 border border-red-200'
    default:
      return 'bg-gray-100 text-gray-600 border border-gray-200'
  }
}

/**
 * 원생 상세 → 콘텐츠 결제 탭 — 이 원생의 콘텐츠 사용료 결제 이력(결제 1건 = 수강권 1행, 최신순).
 * 위 수강권 패널의 누적 지급·사용 수는 이 목록에서 환불 행을 제외한 합이다.
 * 환불 행은 숨기지 않고 흐리게 남기고, 활성 행은 배경으로 강조한다. 행별 환불 액션은 두지 않는다(superadmin 전용).
 */
export function ContentFeeOrderHistoryPanel({ userId }: { userId: number }) {
  const { t, i18n } = useTranslation(['admin'])
  const { data: orders = [], isLoading, isError } = useContentFeeOrders(userId)
  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR'

  const formatAmount = (amount: number | null) =>
    amount != null ? amount.toLocaleString(locale) : '—'
  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString(locale, { timeZone: 'Asia/Seoul' })
  }
  const statusLabel = (status: string) =>
    t(`admin:students.detail.contentFee.orderHistory.status.${status}`, { defaultValue: status })
  const formatCardLast4 = (last4: string | null) => (last4 ? `**** ${last4}` : '—')

  if (isLoading) return null

  return (
    <section className="space-y-2" aria-labelledby="content-fee-order-history-title">
      <h3 id="content-fee-order-history-title" className="text-sm font-medium text-gray-700">
        {t('admin:students.detail.contentFee.orderHistory.title')}
      </h3>
      {isError || orders.length === 0 ? (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500">
          {t('admin:students.detail.contentFee.orderHistory.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t('admin:students.detail.contentFee.orderHistory.col.paidAt')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('admin:students.detail.contentFee.orderHistory.col.level')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('admin:students.detail.contentFee.orderHistory.col.granted')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('admin:students.detail.contentFee.orderHistory.col.carriedOver')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('admin:students.detail.contentFee.orderHistory.col.used')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('admin:students.detail.contentFee.orderHistory.col.remaining')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('admin:students.detail.contentFee.orderHistory.col.amount')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('admin:students.detail.contentFee.orderHistory.col.card')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('admin:students.detail.contentFee.orderHistory.col.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {orders.map((row: ContentFeeOrderRow) => {
                const refunded = row.status === 'refunded'
                return (
                  <tr
                    key={row.entitlementId}
                    data-testid={`content-fee-order-${row.entitlementId}`}
                    data-status={row.status}
                    className={clsx(
                      'text-gray-800',
                      row.status === 'active' && 'bg-primary-50/40',
                      refunded && 'text-gray-400',
                    )}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.paidAt)}</td>
                    <td className="px-3 py-2">{row.level}</td>
                    <td className="px-3 py-2 text-right">{row.grantedLessons}</td>
                    <td className="px-3 py-2 text-right">{row.carriedOver}</td>
                    <td className="px-3 py-2 text-right">{row.usedLessons}</td>
                    <td className="px-3 py-2 text-right">{refunded ? 0 : row.remainingLessons}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatAmount(row.amount)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatCardLast4(row.cardLast4Digits)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-1">
                        <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs', statusClass(row.status))}>
                          {statusLabel(row.status)}
                        </span>
                        {refunded && row.refundedAt ? (
                          <span className="text-xs text-gray-400">{formatDate(row.refundedAt)}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
